import React, { useState, useEffect } from 'react';
import { useTranslationReady } from '../../hooks/useTranslationReady';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantProtection } from '../../hooks/useTenantProtection';
import { useTheme } from '../../contexts/ThemeContext';
import { 
  Plus, 
  Edit2, 
  Trash2, 
  RefreshCw, 
  Search,
  Filter,
  HelpCircle,
  AlertTriangle,
  Clock,
  CheckCircle,
  XCircle,
  User,
  Calendar,
  Eye,
  Settings,
  MessageSquare,
  BarChart3,
  X,
  Paperclip,
  Upload,
  File,
  Link,
  Globe,
  MoreVertical,
  ChevronDown
} from 'lucide-react';
import toast from 'react-hot-toast';
import AttachmentService from '../../components/Attachments/AttachmentService';

const SupportTickets = () => {
  const { t, i18n, ready, translationsReady, safeT } = useTranslationReady([
    'common.save',
    'common.cancel',
    'common.edit',
    'common.delete',
    'common.view',
    'common.filter',
    'common.refresh',
    'common.all',
    'common.showing',
    'common.to',
    'common.of',
    'common.results',
    'common.previous',
    'common.next',
    'common.hide',
    'common.add',
    'common.uploadFiles',
    'common.clickToUpload',
    'common.addUrl',
    'common.selectedAttachments',
    'common.title',
    'common.url',
    'common.description',
    'common.tags',
    'common.tagsPlaceholder',
    'supportTickets.title',
    'supportTickets.subtitle',
    'supportTickets.createTicket',
    'supportTickets.ticketDetails',
    'supportTickets.manageTicket',
    'supportTickets.updateTicket',
    'supportTickets.manage',
    'supportTickets.confirmDelete',
    'supportTickets.searchPlaceholder',
    'supportTickets.descriptionPlaceholder',
    'supportTickets.updatePlaceholder',
    'supportTickets.resolutionPlaceholder',
    'supportTickets.assignToPlaceholder',
    'supportTickets.assignToHelper',
    'supportTickets.attachmentsInfo',
    'supportTickets.fields.title',
    'supportTickets.fields.description',
    'supportTickets.fields.category',
    'supportTickets.fields.status',
    'supportTickets.fields.ticketId',
    'supportTickets.fields.createdBy',
    'supportTickets.fields.assignedTo',
    'supportTickets.fields.created',
    'supportTickets.fields.resolutionNotes',
    'supportTickets.fields.attachments',
    'supportTickets.table.ticket',
    'supportTickets.table.status',
    'supportTickets.table.category',
    'supportTickets.table.createdBy',
    'supportTickets.table.created',
    'supportTickets.categories.featureerror',
    'supportTickets.categories.accountpermissionissue',
    'supportTickets.categories.billingsubscription',
    'supportTickets.categories.featurerequest',
    'supportTickets.categoryDescriptions.featureerror',
    'supportTickets.categoryDescriptions.accountpermissionissue',
    'supportTickets.categoryDescriptions.billingsubscription',
    'supportTickets.categoryDescriptions.featurerequest',
    'supportTickets.statuses.open',
    'supportTickets.statuses.inprogress',
    'supportTickets.statuses.waitingforresponse',
    'supportTickets.statuses.resolved',
    'supportTickets.statuses.closed',
    'supportTickets.stats.total',
    'supportTickets.stats.open',
    'supportTickets.stats.inProgress',
    'supportTickets.stats.resolved',
    'supportTickets.success.created',
    'supportTickets.success.createdWithAttachments',
    'supportTickets.success.updated',
    'supportTickets.success.managed',
    'supportTickets.success.deletedWithAttachments',
    'supportTickets.errors.fetchFailed',
    'supportTickets.errors.detailsFailed',
    'supportTickets.errors.fillRequired',
    'supportTickets.errors.updateRequired'
  ]);
  const { user, hasRole } = useAuth();
  const { getProtectedTenantCode } = useTenantProtection();
  const { isDark } = useTheme();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [stats, setStats] = useState({});
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  
  // Create form state
  const [createForm, setCreateForm] = useState({
    title: '',
    description: '',
    category: 'Feature Error'
  });
  
  // Attachment state for create form
  const [showCreateAttachments, setShowCreateAttachments] = useState(false);
  const [createFormAttachments, setCreateFormAttachments] = useState([]);
  const [showUrlDialog, setShowUrlDialog] = useState(false);
  const [urlForm, setUrlForm] = useState({
    url: '',
    title: '',
    description: '',
    tags: ''
  });
  
  // Update form state (ticket owner edit)
  const [updateForm, setUpdateForm] = useState({
    title: '',
    description: '',
    category: 'Feature Error'
  });

  const resetUpdateForm = () => {
    setUpdateForm({
      title: '',
      description: '',
      category: 'Feature Error'
    });
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setShowUpdateForm(false);
    resetUpdateForm();
  };
  
  // Manage form state (Admin/Manager only)
  const [manageForm, setManageForm] = useState({
    status: '',
    assignedToUserID: '',
    resolutionNotes: ''
  });
  
  // Filters and search
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    category: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [menuExpanded, setMenuExpanded] = useState(false);
  
  // Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  const isAdmin = hasRole('Admin');
  const isManager = hasRole(['Manager', 'Admin', 'Support']);
  const currentUserId = user?.tenantUserId || user?.userId;

  const statusColors = {
    'Open': 'bg-red-100 text-red-800',
    'In Progress': 'bg-yellow-100 text-yellow-800',
    'Waiting for Response': 'bg-teal-100 text-teal-800',
    'Resolved': 'bg-green-100 text-green-800',
    'Closed': 'bg-gray-100 text-gray-800'
  };

  const categoryOptions = [
    'Feature Error',
    'Account & Permission Issue',
    'Billing & Subscription',
    'Feature Request'
  ];

  // Category descriptions for user guidance
  const categoryDescriptions = {
    'Feature Error': safeT('supportTickets.categoryDescriptions.featureerror', 'Use when reporting that a function or system is not working properly, such as: clicking does not respond, data is not saved, system displays incorrectly'),
    'Account & Permission Issue': safeT('supportTickets.categoryDescriptions.accountpermissionissue', 'Issues related to login, data access permissions, password changes, inviting new users'),
    'Billing & Subscription': safeT('supportTickets.categoryDescriptions.billingsubscription', 'Payment matters, package renewal, invoice/receipt issuance'),
    'Feature Request': safeT('supportTickets.categoryDescriptions.featurerequest', 'Suggestions to add new features or improve existing ones')
  };

  const statusOptions = ['Open', 'In Progress', 'Waiting for Response', 'Resolved', 'Closed'];

  // Helper functions for consistent translation key mapping
  const getCategoryTranslationKey = (category) => {
    const keyMap = {
      'Feature Error': 'featureerror',
      'Account & Permission Issue': 'accountpermissionissue',
      'Billing & Subscription': 'billingsubscription',
      'Feature Request': 'featurerequest'
    };
    return keyMap[category] || category.toLowerCase().replace(/\s+/g, '').replace(/&/g, '');
  };

  const getStatusTranslationKey = (status) => {
    const keyMap = {
      'Open': 'open',
      'In Progress': 'inprogress',
      'Waiting for Response': 'waitingforresponse',
      'Resolved': 'resolved',
      'Closed': 'closed'
    };
    return keyMap[status] || status.toLowerCase().replace(/\s+/g, '');
  };

  const fetchTickets = async (resetPage = false) => {
    try {
      setLoading(true);
      const page = resetPage ? 1 : pagination.page;
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString(),
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, value]) => value !== '')
        )
      });
      
      const tenantCode = getProtectedTenantCode();
      
      // DEBUG: Log tenant information
      console.log('🏢 SupportTickets fetchTickets Debug:', {
        tenantCode,
        user: user?.username,
        selectedTenant: user?.selectedTenant,
        userTenants: user?.tenants?.length || 0
      });
      
      const response = await fetch(`/api/support-tickets?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
          ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {})
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      // DEBUG: Log database information from response
      console.log('📊 SupportTickets Response Debug:', {
        ticketsCount: data.tickets?.length || 0,
        databaseInfo: data.databaseInfo || 'Not provided',
        tenantInfo: data.tenantInfo || 'Not provided'
      });
      
      setTickets(data.tickets || []);
      setPagination(data.pagination || pagination);
      setError(null);
    } catch (err) {
      setError(err.message);
      toast.error(safeT('supportTickets.errors.fetchFailed', 'Failed to fetch support tickets'));
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    if (!isManager) return;
    
    try {
      const tenantCode = getProtectedTenantCode();
      const response = await fetch('/api/support-tickets/stats/overview', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
          ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {})
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data || {});
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const fetchTicketDetail = async (ticketId) => {
    try {
      const tenantCode = getProtectedTenantCode();
      const response = await fetch(`/api/support-tickets/${ticketId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
          ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {})
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const ticketData = await response.json();
      
      setSelectedTicket(ticketData);
      setUpdateForm({
        title: ticketData.Title || '',
        description: ticketData.Description || '',
        category: ticketData.Category || 'Feature Error'
      });
      setShowUpdateForm(false);
      return ticketData;
    } catch (err) {
      toast.error(safeT('supportTickets.errors.detailsFailed', 'Failed to fetch ticket details'));
      throw err;
    }
  };

  useEffect(() => {
    fetchTickets();
    fetchStats();
  }, []);

  useEffect(() => {
    fetchTickets();
  }, [pagination.page]);

  // Auto-show filter bar when filters are active
  useEffect(() => {
    const hasActiveFilters = Object.values(filters).some(value => value !== '');
    if (hasActiveFilters) {
      setShowFilters(true);
    }
  }, [filters]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchTickets(true);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      status: '',
      category: ''
    });
    setTimeout(() => fetchTickets(true), 100);
  };

  // Attachment handling functions for create form
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const attachment = {
        id: Date.now() + Math.random(),
        type: 'file',
        file: file,
        title: file.name,
        description: '',
        tags: '',
        size: file.size,
        uploaded: false
      };
      setCreateFormAttachments(prev => [...prev, attachment]);
    });
  };

  const handleUrlAdd = () => {
    if (!urlForm.url.trim()) {
      toast.error('URL is required');
      return;
    }

    const attachment = {
      id: Date.now() + Math.random(),
      type: 'url',
      url: urlForm.url,
      title: urlForm.title || urlForm.url,
      description: urlForm.description,
      tags: urlForm.tags,
      uploaded: false
    };

    setCreateFormAttachments(prev => [...prev, attachment]);
    setUrlForm({ url: '', title: '', description: '', tags: '' });
    setShowUrlDialog(false);
  };

  const removeAttachment = (id) => {
    setCreateFormAttachments(prev => prev.filter(att => att.id !== id));
  };

  const updateAttachment = (id, updates) => {
    setCreateFormAttachments(prev => 
      prev.map(att => att.id === id ? { ...att, ...updates } : att)
    );
  };

  const uploadAttachments = async (ticketId) => {
    console.log('🔍 Starting attachment upload for ticket:', ticketId);
    console.log('🔍 Attachments to upload:', createFormAttachments);
    
    const uploadPromises = createFormAttachments.map(async (attachment) => {
      try {
        if (attachment.type === 'file') {
          console.log('🔍 Uploading file attachment:', attachment.title, attachment.file);
          
          const formData = new FormData();
          formData.append('file', attachment.file);
          formData.append('title', attachment.title);
          formData.append('description', attachment.description);
          formData.append('tags', attachment.tags);

          const tenantCode = getProtectedTenantCode();
          console.log('🔍 Tenant code:', tenantCode);
          console.log('🔍 Upload URL:', `/api/attachments/SupportTicket/${ticketId}/upload`);
          
          const response = await fetch(`/api/attachments/SupportTicket/${ticketId}/upload`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {})
            },
            body: formData
          });

          console.log('🔍 Upload response status:', response.status);
          
          if (!response.ok) {
            const errorText = await response.text();
            console.error('🔍 Upload error response:', errorText);
            throw new Error(`Failed to upload ${attachment.title}: ${errorText}`);
          }
          
          const result = await response.json();
          console.log('🔍 Upload success result:', result);
        } else if (attachment.type === 'url') {
          console.log('🔍 Uploading URL attachment:', attachment.title, attachment.url);
          
          const tenantCode = getProtectedTenantCode();
          const response = await fetch(`/api/attachments/SupportTicket/${ticketId}/url`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json',
              ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {})
            },
            body: JSON.stringify({
              url: attachment.url,
              title: attachment.title,
              description: attachment.description,
              tags: attachment.tags
            })
          });

          console.log('🔍 URL upload response status:', response.status);
          
          if (!response.ok) {
            const errorText = await response.text();
            console.error('🔍 URL upload error response:', errorText);
            throw new Error(`Failed to add URL ${attachment.title}: ${errorText}`);
          }
          
          const result = await response.json();
          console.log('🔍 URL upload success result:', result);
        }
      } catch (error) {
        console.error('🔍 Attachment upload error:', error);
        throw error;
      }
    });

    return Promise.all(uploadPromises);
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    
    if (!createForm.title.trim() || !createForm.description.trim()) {
      toast.error(safeT('supportTickets.errors.fillRequired', 'Please fill in all required fields'));
      return;
    }

    try {
      console.log('🔍 Creating support ticket with data:', createForm);
      console.log('🔍 Attachments to upload:', createFormAttachments);
      
      const tenantCode = getProtectedTenantCode();
      const response = await fetch('/api/support-tickets', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
          ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {})
        },
        body: JSON.stringify(createForm)
      });

      console.log('🔍 Ticket creation response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('🔍 Ticket creation error:', errorData);
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }

      const result = await response.json();
      console.log('🔍 Ticket creation result:', result);
      const newTicket = result.ticket;
      console.log('🔍 New ticket ID:', newTicket.TicketID);

      // Upload attachments if any
      if (createFormAttachments.length > 0) {
        try {
          console.log('🔍 Starting attachment upload process...');
          await uploadAttachments(newTicket.TicketID);
          console.log('🔍 Attachment upload completed successfully');
          toast.success(safeT('supportTickets.success.createdWithAttachments', 'Support ticket created successfully with attachments'));
        } catch (uploadError) {
          console.error('🔍 Failed to upload attachments:', uploadError);
          toast.error('Ticket created but some attachments failed to upload');
        }
      } else {
        console.log('🔍 No attachments to upload');
        toast.success(safeT('supportTickets.success.created', 'Support ticket created successfully'));
      }

      setShowCreateForm(false);
      setCreateForm({
        title: '',
        description: '',
        category: 'Feature Error'
      });
      setShowCreateAttachments(false);
      setCreateFormAttachments([]);
      setUrlForm({ url: '', title: '', description: '', tags: '' });
      fetchTickets(true);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    
    if (!updateForm.title.trim() || !updateForm.description.trim()) {
      toast.error(safeT('supportTickets.errors.updateRequired', 'Please enter an update'));
      return;
    }

    try {
      const tenantCode = getProtectedTenantCode();
      const response = await fetch(`/api/support-tickets/${selectedTicket.TicketID}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
          ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {})
        },
        body: JSON.stringify(updateForm)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }

      toast.success(safeT('supportTickets.success.updated', 'Support ticket updated successfully'));
      closeDetailModal();
      fetchTickets();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleManageSubmit = async (e) => {
    e.preventDefault();

    try {
      const tenantCode = getProtectedTenantCode();
      const response = await fetch(`/api/support-tickets/${selectedTicket.TicketID}/manage`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
          ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {})
        },
        body: JSON.stringify(manageForm)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }

      toast.success(safeT('supportTickets.success.managed', 'Support ticket managed successfully'));
      setManageForm({
        status: '',
        assignedToUserID: '',
        resolutionNotes: ''
      });
      setShowManageModal(false);
      fetchTickets();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDeleteTicket = async (ticketId) => {
    if (!window.confirm(safeT('supportTickets.confirmDelete', 'Are you sure you want to delete this ticket?'))) return;

    try {
      const tenantCode = getProtectedTenantCode();
      const response = await fetch(`/api/support-tickets/${ticketId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
          ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {})
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`);
      }

      toast.success(safeT('supportTickets.success.deletedWithAttachments', 'Support ticket and associated attachments deleted successfully'));
      fetchTickets();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleViewDetails = async (ticket) => {
    try {
      await fetchTicketDetail(ticket.TicketID);
      setShowDetailModal(true);
    } catch (err) {
      // Error handled in fetchTicketDetail
    }
  };

  const handleManageTicket = async (ticket) => {
    try {
      await fetchTicketDetail(ticket.TicketID);
      setManageForm({
        status: ticket.Status,
        assignedToUserID: ticket.AssignedToUserID || '',
        resolutionNotes: ticket.ResolutionNotes || ''
      });
      setShowManageModal(true);
    } catch (err) {
      // Error handled in fetchTicketDetail
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString() + ' ' + new Date(dateString).toLocaleTimeString();
  };

  if (loading && tickets.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header - Title and Actions in Same Line */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-4">
          {/* Title Section */}
          <div className="flex-shrink-0">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{safeT('supportTickets.title', 'Support Tickets')}</h1>
            <p className="text-gray-600 dark:text-gray-300 mt-1">{safeT('supportTickets.subtitle', 'Manage support requests and issues')}</p>
          </div>
          
          {/* Action Buttons - Collapsible Menu */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-2">
            {/* Filter Button - Between View Toggle and Action Buttons */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-3 py-2 rounded-lg flex items-center justify-center space-x-2 text-xs sm:text-sm font-medium touch-target no-tap-highlight transition-all ${
                Object.values(filters).some(value => value !== '')
                  ? 'bg-orange-500 dark:bg-orange-600 text-white hover:bg-orange-600 dark:hover:bg-orange-700'
                  : showFilters
                  ? 'bg-gray-600 dark:bg-gray-500 text-white hover:bg-gray-700 dark:hover:bg-gray-600'
                  : 'bg-gray-500 dark:bg-gray-600 text-white hover:bg-gray-600 dark:hover:bg-gray-700'
              }`}
              title="Toggle filters"
            >
              <Filter className="h-4 w-4 flex-shrink-0" />
              <span className="hidden sm:inline">{safeT('common.filter', 'Filter')}</span>
              {Object.values(filters).some(value => value !== '') && (
                <span className="bg-white/20 px-1.5 py-0.5 rounded-full text-xs font-bold">
                  {Object.values(filters).filter(value => value !== '').length}
                </span>
              )}
            </button>
            
            {/* Action Buttons Row */}
            <div className="flex flex-wrap items-center justify-end gap-2">
              {/* Expandable Menu Buttons - Always visible on mobile, collapsible on desktop */}
              <div className={`flex flex-wrap items-center gap-2 ${menuExpanded ? 'sm:flex' : 'sm:hidden'}`}>
                <button 
                  onClick={() => fetchTickets()}
                  className="bg-gray-600 dark:bg-gray-500 text-white px-3 py-2 rounded-lg flex items-center justify-center space-x-1 sm:space-x-2 text-xs sm:text-sm hover:bg-gray-700 dark:hover:bg-gray-600 touch-target no-tap-highlight"
                >
                  <RefreshCw className="h-4 w-4 flex-shrink-0" />
                  <span className="hidden sm:inline">{safeT('common.refresh', 'Refresh')}</span>
                  <span className="sm:hidden">Refresh</span>
                </button>
              </div>
              
              {/* Expand/Collapse Button - Only on Desktop */}
              <button
                onClick={() => setMenuExpanded(!menuExpanded)}
                className="hidden sm:flex bg-gray-500 dark:bg-gray-600 text-white px-3 py-2 rounded-lg hover:bg-gray-600 dark:hover:bg-gray-700 items-center justify-center space-x-1 sm:space-x-2 text-xs sm:text-sm touch-target no-tap-highlight transition-transform"
                title={menuExpanded ? 'Collapse menu' : 'Expand menu'}
              >
                <MoreVertical className="h-4 w-4 flex-shrink-0" />
                <ChevronDown className={`h-3 w-3 flex-shrink-0 transition-transform duration-200 ${menuExpanded ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Create Ticket Button - Always Visible */}
              <button 
                onClick={() => setShowCreateForm(true)}
                className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 dark:from-teal-600 dark:to-cyan-600 dark:hover:from-teal-700 dark:hover:to-cyan-700 text-white px-4 sm:px-5 py-2.5 sm:py-3 rounded-lg flex items-center justify-center space-x-2 text-sm sm:text-base font-semibold touch-target no-tap-highlight shadow-md transition-all duration-200"
              >
                <Plus className="h-5 w-5 flex-shrink-0" />
                <span>{safeT('supportTickets.createTicket', 'Create Ticket')}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Stats (Admin/Manager only) */}
        {isManager && Object.keys(stats).length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <HelpCircle className="h-8 w-8 text-teal-600 dark:text-teal-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                        {safeT('supportTickets.stats.total', 'Total Tickets')}
                      </dt>
                      <dd className="text-lg font-medium text-gray-900 dark:text-white">
                        {stats.totalTickets || 0}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <AlertTriangle className="h-8 w-8 text-red-600 dark:text-red-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                        {safeT('supportTickets.stats.open', 'Open')}
                      </dt>
                      <dd className="text-lg font-medium text-gray-900 dark:text-white">
                        {stats.openTickets || 0}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Clock className="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                        {safeT('supportTickets.stats.inProgress', 'In Progress')}
                      </dt>
                      <dd className="text-lg font-medium text-gray-900 dark:text-white">
                        {stats.inProgressTickets || 0}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                        {safeT('supportTickets.stats.resolved', 'Resolved')}
                      </dt>
                      <dd className="text-lg font-medium text-gray-900 dark:text-white">
                        {stats.resolvedTickets || 0}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Search and Filters - Hidden by Default */}
        {showFilters && (
          <div className={`p-3 sm:p-4 rounded-lg shadow border transition-colors duration-200 ${
            Object.values(filters).some(value => value !== '')
              ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-300 dark:border-orange-700'
              : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
          }`}>
            {Object.values(filters).some(value => value !== '') && (
              <div className="mb-2 flex items-center gap-2">
                <span className="text-xs font-medium text-orange-700 dark:text-orange-300">
                  🔍 Filters Active
                </span>
                <button
                  type="button"
                  onClick={clearFilters}
                  className="text-xs text-orange-600 dark:text-orange-400 hover:underline"
                >
                  Clear All
                </button>
              </div>
            )}
            <form onSubmit={handleSearch} className="mb-4">
              <div className="flex items-center space-x-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 h-4 w-4" />
                    <input
                      type="text"
                      placeholder={safeT('supportTickets.searchPlaceholder', 'Search tickets by title or description...')}
                      value={filters.search}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                      className="pl-10 w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  className="px-4 py-2 bg-gray-600 dark:bg-gray-500 text-white rounded-md hover:bg-gray-700 dark:hover:bg-gray-600 flex items-center justify-center space-x-1 sm:space-x-2 text-sm sm:text-base touch-target no-tap-highlight"
                >
                  <Filter className="h-4 w-4" />
                  <span>{safeT('common.filter', 'Filter')}</span>
                </button>
              </div>
            </form>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {safeT('supportTickets.fields.status', 'Status')}
                </label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">{safeT('common.all', 'All')}</option>
                  {statusOptions.map(status => (
                    <option key={status} value={status}>
                      {safeT(`supportTickets.statuses.${getStatusTranslationKey(status)}`, status)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {safeT('supportTickets.fields.category', 'Category')}
                </label>
                <select
                  value={filters.category}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">{safeT('common.all', 'All')}</option>
                  {categoryOptions.map(category => (
                    <option key={category} value={category}>
                      {safeT(`supportTickets.categories.${getCategoryTranslationKey(category)}`, category)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Tickets Table */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {safeT('supportTickets.table.ticket', 'Ticket')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {safeT('supportTickets.table.status', 'Status')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {safeT('supportTickets.table.category', 'Category')}
                </th>
                {isManager && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {safeT('supportTickets.table.createdBy', 'Created By')}
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {safeT('supportTickets.table.created', 'Created')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {safeT('common.actions', 'Actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {tickets.map((ticket) => (
                <tr key={ticket.TicketID} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                     <td className="px-6 py-4 whitespace-nowrap">
                     <div>
                       <div className="text-sm font-medium text-gray-900 dark:text-white">
                         <button
                           onClick={() => handleViewDetails(ticket)}
                           className="text-teal-600 dark:text-teal-400 hover:text-teal-900 dark:hover:text-teal-300 hover:underline cursor-pointer"
                         >
                           {ticket.Title}
                         </button>
                         <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">#{ticket.TicketID}</span>
                         {ticket.HasAttachments && (
                           <span className="ml-2 inline-flex items-center text-xs text-teal-600 dark:text-teal-400">
                             <Paperclip className="h-3 w-3 mr-1" />
                             {safeT('supportTickets.fields.attachments', 'Attachments')}
                           </span>
                         )}
                       </div>
                       <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
                         {ticket.Description}
                       </div>
                     </div>
                   </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusColors[ticket.Status]}`}>
                      {safeT(`supportTickets.statuses.${getStatusTranslationKey(ticket.Status)}`, ticket.Status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {safeT(`supportTickets.categories.${getCategoryTranslationKey(ticket.Category)}`, ticket.Category)}
                  </td>
                  {isManager && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <User className="h-4 w-4 text-gray-400 mr-2" />
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {ticket.CreatedByName}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {ticket.CreatedByEmail}
                          </div>
                        </div>
                      </div>
                    </td>
                  )}
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-2" />
                      {formatDate(ticket.CreatedAt)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      {ticket.HasAttachments && (
                        <span className="text-teal-600 dark:text-teal-400" title={safeT('supportTickets.fields.attachments', 'Attachments')}>
                          <Paperclip className="h-4 w-4" />
                        </span>
                      )}
                      <button
                        onClick={() => handleViewDetails(ticket)}
                        className="text-teal-600 dark:text-teal-400 hover:text-teal-900 dark:hover:text-teal-300"
                        title={safeT('common.view', 'View')}
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      {isManager && (
                        <button
                          onClick={() => handleManageTicket(ticket)}
                          className="text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300"
                          title={safeT('supportTickets.manage', 'Manage')}
                        >
                          <Settings className="h-4 w-4" />
                        </button>
                      )}
                      {isAdmin && (
                        <button
                          onClick={() => handleDeleteTicket(ticket.TicketID)}
                          className="text-red-600 hover:text-red-900"
                          title={safeT('common.delete', 'Delete')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                disabled={pagination.page <= 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
              >
                {safeT('common.previous', 'Previous')}
              </button>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.pages, prev.page + 1) }))}
                disabled={pagination.page >= pagination.pages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
              >
                {safeT('common.next', 'Next')}
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  {safeT('common.showing', 'Showing')} <span className="font-medium">{((pagination.page - 1) * pagination.limit) + 1}</span> {safeT('common.to', 'to')} <span className="font-medium">{Math.min(pagination.page * pagination.limit, pagination.total)}</span> {safeT('common.of', 'of')} <span className="font-medium">{pagination.total}</span> {safeT('common.results', 'results')}
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                    disabled={pagination.page <= 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                  >
                    {safeT('common.previous', 'Previous')}
                  </button>
                  {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                    const page = i + 1;
                    return (
                      <button
                        key={page}
                        onClick={() => setPagination(prev => ({ ...prev, page }))}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          page === pagination.page
                            ? 'z-10 bg-teal-50 dark:bg-teal-900/20 border-teal-500 dark:border-teal-400 text-teal-600 dark:text-teal-400'
                            : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.pages, prev.page + 1) }))}
                    disabled={pagination.page >= pagination.pages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                  >
                    {safeT('common.next', 'Next')}
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

             {/* Create Ticket Modal */}
       {showCreateForm && (
         <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
           <div className="relative top-20 mx-auto p-5 border border-gray-200 dark:border-gray-700 w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white dark:bg-gray-800">
             <div className="flex justify-between items-center mb-4">
               <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                 {safeT('supportTickets.createTicket', 'Create Ticket')}
               </h3>
               <button
                 onClick={() => setShowCreateForm(false)}
                 className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
               >
                 <X className="h-6 w-6" />
               </button>
             </div>

            <form onSubmit={handleCreateSubmit}>
                             <div className="space-y-4">
                 <div>
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                     {safeT('supportTickets.fields.title', 'Title')} *
                   </label>
                   <input
                     type="text"
                     value={createForm.title}
                     onChange={(e) => setCreateForm(prev => ({ ...prev, title: e.target.value }))}
                     className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                     required
                     maxLength={200}
                   />
                 </div>

                 <div>
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                     {safeT('supportTickets.fields.category', 'Category')}
                   </label>
                   <select
                     value={createForm.category}
                     onChange={(e) => setCreateForm(prev => ({ ...prev, category: e.target.value }))}
                     className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                   >
                     {categoryOptions.map(category => (
                       <option key={category} value={category}>
                         {safeT(`supportTickets.categories.${getCategoryTranslationKey(category)}`, category)}
                       </option>
                     ))}
                   </select>
                   {createForm.category && categoryDescriptions[createForm.category] && (
                     <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
                       <div className="flex items-start">
                         <HelpCircle className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 mr-2 flex-shrink-0" />
                         <p className="text-sm text-blue-800 dark:text-blue-300">
                           {categoryDescriptions[createForm.category]}
                         </p>
                       </div>
                     </div>
                   )}
                 </div>

                 <div>
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                     {safeT('supportTickets.fields.description', 'Description')} *
                   </label>
                   <textarea
                     value={createForm.description}
                     onChange={(e) => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
                     rows={6}
                     className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                     required
                     placeholder={safeT('supportTickets.descriptionPlaceholder', 'Please describe your issue in detail...')}
                   />
                 </div>

                 {/* Attachments Section */}
                 <div>
                   <div className="flex items-center justify-between mb-2">
                     <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                       {safeT('supportTickets.fields.attachments', 'Attachments')}
                     </label>
                     <button
                       type="button"
                       onClick={() => setShowCreateAttachments(!showCreateAttachments)}
                       className="flex items-center space-x-1 text-sm text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-300"
                     >
                       <Paperclip className="h-4 w-4" />
                       <span>{showCreateAttachments ? safeT('common.hide', 'Hide') : safeT('common.add', 'Add')}</span>
                     </button>
                   </div>
                   
                   {showCreateAttachments && (
                     <div className="border border-gray-300 dark:border-gray-600 rounded-md p-4 bg-gray-50 dark:bg-gray-700">
                       <div className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                         {safeT('supportTickets.attachmentsInfo', 'Add files, images, or links to support your ticket. Attachments will be uploaded when the ticket is created.')}
                       </div>
                       
                       {/* File Upload */}
                       <div className="mb-4">
                         <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                           {safeT('common.uploadFiles', 'Upload Files')}
                         </label>
                         <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 text-center hover:border-teal-400 dark:hover:border-teal-500 transition-colors">
                           <input
                             type="file"
                             multiple
                             onChange={handleFileSelect}
                             className="hidden"
                             id="file-upload"
                           />
                           <label htmlFor="file-upload" className="cursor-pointer">
                             <Upload className="h-8 w-8 text-gray-400 dark:text-gray-500 mx-auto mb-2" />
                             <p className="text-sm text-gray-600 dark:text-gray-400">
                               {safeT('common.clickToUpload', 'Click to upload files')}
                             </p>
                           </label>
                         </div>
                       </div>

                       {/* Add URL */}
                       <div className="mb-4">
                         <button
                           type="button"
                           onClick={() => setShowUrlDialog(true)}
                           className="flex items-center space-x-2 text-sm text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-300"
                         >
                           <Link className="h-4 w-4" />
                           <span>{safeT('common.addUrl', 'Add URL')}</span>
                         </button>
                       </div>

                       {/* Attachments List */}
                       {createFormAttachments.length > 0 && (
                         <div className="space-y-2">
                           <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                             {safeT('common.selectedAttachments', 'Selected Attachments')} ({createFormAttachments.length})
                           </h4>
                           {createFormAttachments.map((attachment) => (
                             <div key={attachment.id} className="flex items-center justify-between p-2 bg-white dark:bg-gray-600 rounded border">
                               <div className="flex items-center space-x-2 flex-1">
                                 {attachment.type === 'file' ? (
                                   <File className="h-4 w-4 text-gray-500" />
                                 ) : (
                                   <Globe className="h-4 w-4 text-gray-500" />
                                 )}
                                 <div className="flex-1 min-w-0">
                                   <input
                                     type="text"
                                     value={attachment.title}
                                     onChange={(e) => updateAttachment(attachment.id, { title: e.target.value })}
                                     className="text-sm bg-transparent border-none focus:outline-none focus:ring-0 w-full"
                                     placeholder={safeT('common.title', 'Title')}
                                   />
                                   {attachment.type === 'file' && (
                                     <p className="text-xs text-gray-500">
                                       {(attachment.size / 1024 / 1024).toFixed(2)} MB
                                     </p>
                                   )}
                                 </div>
                               </div>
                               <button
                                 type="button"
                                 onClick={() => removeAttachment(attachment.id)}
                                 className="text-red-500 hover:text-red-700"
                               >
                                 <X className="h-4 w-4" />
                               </button>
                             </div>
                           ))}
                         </div>
                       )}
                     </div>
                   )}
                 </div>
               </div>

                             <div className="flex justify-end space-x-3 mt-6">
                 <button
                   type="button"
                   onClick={() => setShowCreateForm(false)}
                   className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                 >
                   {safeT('common.cancel', 'Cancel')}
                 </button>
                 <button
                   type="submit"
                   className="px-4 py-2 bg-teal-600 dark:bg-teal-500 text-white rounded-md hover:bg-teal-700 dark:hover:bg-teal-600"
                 >
                   {safeT('supportTickets.createTicket', 'Create Ticket')}
                 </button>
               </div>
            </form>
          </div>
        </div>
      )}

             {/* Ticket Detail Modal */}
       {showDetailModal && selectedTicket && (
         <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
           <div className="relative top-10 mx-auto p-5 border border-gray-200 dark:border-gray-700 w-11/12 md:w-3/4 lg:w-2/3 shadow-lg rounded-md bg-white dark:bg-gray-800 max-h-screen overflow-y-auto">
             <div className="flex justify-between items-center mb-4">
               <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                 {safeT('supportTickets.ticketDetails', 'Ticket Details')}
               </h3>
               <div className="flex items-center space-x-3">
                 {(selectedTicket.UserID === currentUserId) && selectedTicket.Status !== 'Closed' && (
                   <button
                     type="button"
                     onClick={() => setShowUpdateForm(prev => !prev)}
                     className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 dark:bg-teal-500 dark:hover:bg-teal-600"
                   >
                     <Edit2 className="h-4 w-4 mr-1" />
                     {showUpdateForm ? safeT('common.close', 'Close') : safeT('supportTickets.updateTicket', 'Update Ticket')}
                   </button>
                 )}
                 {isManager && (
                   <button
                     type="button"
                     onClick={() => handleManageTicket(selectedTicket)}
                     className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600"
                   >
                     <Settings className="h-4 w-4 mr-1" />
                     {safeT('supportTickets.manage', 'Manage')}
                   </button>
                 )}
               <button
                onClick={closeDetailModal}
                 className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
               >
                 <X className="h-6 w-6" />
               </button>
               </div>
             </div>

            <div className="space-y-4">
                             {/* Ticket Info */}
               <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                 <div>
                   <h4 className="font-medium text-gray-900 dark:text-white mb-2">{safeT('supportTickets.fields.title', 'Title')}</h4>
                   <p className="text-gray-700 dark:text-gray-300">{selectedTicket.Title}</p>
                 </div>
                 <div>
                   <h4 className="font-medium text-gray-900 dark:text-white mb-2">{safeT('supportTickets.fields.ticketId', 'Ticket ID')}</h4>
                   <p className="text-gray-700 dark:text-gray-300">#{selectedTicket.TicketID}</p>
                 </div>
                 <div>
                   <h4 className="font-medium text-gray-900 dark:text-white mb-2">{safeT('supportTickets.fields.status', 'Status')}</h4>
                   <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusColors[selectedTicket.Status]}`}>
                     {safeT(`supportTickets.statuses.${getStatusTranslationKey(selectedTicket.Status)}`, selectedTicket.Status)}
                   </span>
                 </div>
                 <div>
                   <h4 className="font-medium text-gray-900 dark:text-white mb-2">{safeT('supportTickets.fields.category', 'Category')}</h4>
                   <p className="text-gray-700 dark:text-gray-300">{safeT(`supportTickets.categories.${getCategoryTranslationKey(selectedTicket.Category)}`, selectedTicket.Category)}</p>
                 </div>
                 <div>
                   <h4 className="font-medium text-gray-900 dark:text-white mb-2">{safeT('supportTickets.fields.created', 'Created')}</h4>
                   <p className="text-gray-700 dark:text-gray-300">{formatDate(selectedTicket.CreatedAt)}</p>
                 </div>
                                 {isManager && (
                   <>
                     <div>
                       <h4 className="font-medium text-gray-900 dark:text-white mb-2">{safeT('supportTickets.fields.createdBy', 'Created By')}</h4>
                       <p className="text-gray-700 dark:text-gray-300">{selectedTicket.CreatedByName} ({selectedTicket.CreatedByEmail})</p>
                     </div>
                     {selectedTicket.AssignedToName && (
                       <div>
                         <h4 className="font-medium text-gray-900 dark:text-white mb-2">{safeT('supportTickets.fields.assignedTo', 'Assigned To')}</h4>
                         <p className="text-gray-700 dark:text-gray-300">{selectedTicket.AssignedToName} ({selectedTicket.AssignedToEmail})</p>
                       </div>
                     )}
                   </>
                 )}
              </div>

                             {/* Description */}
               <div>
                 <h4 className="font-medium text-gray-900 dark:text-white mb-2">{safeT('supportTickets.fields.description', 'Description')}</h4>
                 <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-md">
                   <pre className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">{selectedTicket.Description}</pre>
                 </div>
               </div>

                             {/* Attachments */}
               <div>
                 <h4 className="font-medium text-gray-900 dark:text-white mb-2 flex items-center">
                   <Paperclip className="h-4 w-4 mr-2" />
                   {safeT('supportTickets.fields.attachments', 'Attachments')}
                 </h4>
                 <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-md">
                   <AttachmentService 
                     entityType="SupportTicket" 
                     entityId={selectedTicket.TicketID} 
                     onAttachmentChange={() => {
                       // Refresh ticket data if needed
                       fetchTicketDetail(selectedTicket.TicketID);
                     }}
                   />
                 </div>
               </div>

                             {/* Resolution Notes (if any) */}
               {selectedTicket.ResolutionNotes && (
                 <div>
                   <h4 className="font-medium text-gray-900 dark:text-white mb-2">{safeT('supportTickets.fields.resolutionNotes', 'Resolution Notes')}</h4>
                   <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-md">
                     <pre className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">{selectedTicket.ResolutionNotes}</pre>
                   </div>
                 </div>
               )}

                             {/* Update Form (for users to add updates) */}
              {(selectedTicket.UserID === currentUserId) && selectedTicket.Status !== 'Closed' && showUpdateForm && (
                 <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                    {safeT('supportTickets.editTicket', safeT('supportTickets.addUpdate', 'Add Update'))}
                  </h4>
                  <form onSubmit={handleUpdateSubmit}>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          {safeT('supportTickets.fields.title', 'Title')} *
                        </label>
                        <input
                          type="text"
                          value={updateForm.title}
                          onChange={(e) => setUpdateForm(prev => ({ ...prev, title: e.target.value }))}
                          className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          maxLength={200}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          {safeT('supportTickets.fields.category', 'Category')}
                        </label>
                        <select
                          value={updateForm.category}
                          onChange={(e) => setUpdateForm(prev => ({ ...prev, category: e.target.value }))}
                          className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                          {categoryOptions.map(category => (
                            <option key={category} value={category}>
                              {safeT(`supportTickets.categories.${getCategoryTranslationKey(category)}`, category)}
                            </option>
                          ))}
                        </select>
                        {updateForm.category && categoryDescriptions[updateForm.category] && (
                          <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
                            <div className="flex items-start">
                              <HelpCircle className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 mr-2 flex-shrink-0" />
                              <p className="text-sm text-blue-800 dark:text-blue-300">
                                {categoryDescriptions[updateForm.category]}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          {safeT('supportTickets.fields.description', 'Description')} *
                        </label>
                        <textarea
                          value={updateForm.description}
                          onChange={(e) => setUpdateForm(prev => ({ ...prev, description: e.target.value }))}
                          rows={4}
                          className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder={safeT('supportTickets.updatePlaceholder', 'Add your update or additional information...')}
                          required
                        />
                      </div>
                    </div>
                    <div className="flex justify-end mt-3">
                      <button
                        type="submit"
                        className="px-4 py-2 bg-teal-600 dark:bg-teal-500 text-white rounded-md hover:bg-teal-700 dark:hover:bg-teal-600"
                      >
                        <Edit2 className="h-4 w-4 inline mr-2" />
                        {safeT('supportTickets.updateTicket', 'Update Ticket')}
                      </button>
                    </div>
                  </form>
                </div>
               )}
            </div>
          </div>
        </div>
      )}

             {/* Manage Ticket Modal (Admin/Manager only) */}
       {showManageModal && selectedTicket && isManager && (
         <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
           <div className="relative top-20 mx-auto p-5 border border-gray-200 dark:border-gray-700 w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white dark:bg-gray-800">
             <div className="flex justify-between items-center mb-4">
               <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                 {safeT('supportTickets.manageTicket', 'Manage Ticket')} #{selectedTicket.TicketID}
               </h3>
               <button
                 onClick={() => setShowManageModal(false)}
                 className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
               >
                 <X className="h-6 w-6" />
               </button>
             </div>

            <form onSubmit={handleManageSubmit}>
                             <div className="space-y-4">
                 <div>
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                     {safeT('supportTickets.fields.status', 'Status')}
                   </label>
                   <select
                     value={manageForm.status}
                     onChange={(e) => setManageForm(prev => ({ ...prev, status: e.target.value }))}
                     className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                   >
                     {statusOptions.map(status => (
                       <option key={status} value={status}>
                         {safeT(`supportTickets.statuses.${getStatusTranslationKey(status)}`, status)}
                       </option>
                     ))}
                   </select>
                 </div>

                 <div>
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                     {safeT('supportTickets.fields.assignedTo', 'Assigned To')}
                   </label>
                   <input
                     type="number"
                     value={manageForm.assignedToUserID}
                     onChange={(e) => setManageForm(prev => ({ ...prev, assignedToUserID: e.target.value }))}
                     className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                     placeholder={safeT('supportTickets.assignToPlaceholder', 'Enter User ID to assign')}
                   />
                   <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                     {safeT('supportTickets.assignToHelper', 'Leave empty to unassign')}
                   </p>
                 </div>

                 <div>
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                     {safeT('supportTickets.fields.resolutionNotes', 'Resolution Notes')}
                   </label>
                   <textarea
                     value={manageForm.resolutionNotes}
                     onChange={(e) => setManageForm(prev => ({ ...prev, resolutionNotes: e.target.value }))}
                     rows={4}
                     className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                     placeholder={safeT('supportTickets.resolutionPlaceholder', 'Add resolution notes...')}
                   />
                 </div>
               </div>

                             <div className="flex justify-end space-x-3 mt-6">
                 <button
                   type="button"
                   onClick={() => setShowManageModal(false)}
                   className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                 >
                   {safeT('common.cancel', 'Cancel')}
                 </button>
                 <button
                   type="submit"
                   className="px-4 py-2 bg-green-600 dark:bg-green-500 text-white rounded-md hover:bg-green-700 dark:hover:bg-green-600"
                 >
                   {safeT('supportTickets.updateTicket', 'Update Ticket')}
                 </button>
               </div>
            </form>
          </div>
        </div>
      )}

      {/* URL Dialog Modal */}
      {showUrlDialog && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border border-gray-200 dark:border-gray-700 w-11/12 md:w-1/2 lg:w-1/3 shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                {safeT('common.addUrl', 'Add URL')}
              </h3>
              <button
                onClick={() => setShowUrlDialog(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {safeT('common.url', 'URL')} *
                </label>
                <input
                  type="url"
                  value={urlForm.url}
                  onChange={(e) => setUrlForm(prev => ({ ...prev, url: e.target.value }))}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="https://example.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {safeT('common.title', 'Title')}
                </label>
                <input
                  type="text"
                  value={urlForm.title}
                  onChange={(e) => setUrlForm(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder={safeT('common.title', 'Title')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {safeT('common.description', 'Description')}
                </label>
                <textarea
                  value={urlForm.description}
                  onChange={(e) => setUrlForm(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder={safeT('common.description', 'Description')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {safeT('common.tags', 'Tags')}
                </label>
                <input
                  type="text"
                  value={urlForm.tags}
                  onChange={(e) => setUrlForm(prev => ({ ...prev, tags: e.target.value }))}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder={safeT('common.tagsPlaceholder', 'tag1, tag2, tag3')}
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={() => setShowUrlDialog(false)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                {safeT('common.cancel', 'Cancel')}
              </button>
              <button
                type="button"
                onClick={handleUrlAdd}
                className="px-4 py-2 bg-teal-600 dark:bg-teal-500 text-white rounded-md hover:bg-teal-700 dark:hover:bg-teal-600"
              >
                {safeT('common.add', 'Add')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportTickets;
